<template>
  <b-container fluid>
    <b-row>
      <b-col class="d-flex">
        <backlink class="mt-20" />
        <div class="flex-grow-1">
          <h1>
            Feuille d'expédition
            <div v-if="!isBusy" class="subtitle">#{{ values.shipping_number }}</div>
            <b-badge variant="primary">{{ values.status }}</b-badge>
          </h1>
        </div>
        <div v-if="values.id" class="text-right">
          <b-button variant="outline-primary ml-10" size="lg" @click="openFormModal(values.id)">
            Modifier la fiche
            <i class="far fa-pencil fa-lg ml-10"></i>
          </b-button>
          <b-button variant="outline-primary ml-10" size="lg" v-if="!config.hideActionPdfButton"
            @click="$root.exportPdf(exportPdfData)">
            Exporter en PDF
            <i class="far fa-file-alt fa-lg ml-10"></i>
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-row v-else>
      <b-col>
        <div class="ibox">
          <div class="ibox-content divided">
            <b-row>
              <b-col cols="5" class="d-flex flex-column">
                <h3>Commande</h3>
                <b-row class="flex-grow-1">
                  <b-col>
                    <dl>
                      <dt>{{ renting_labels.renting_number }} :</dt>
                      <dd>{{ rentingValues.renting_number || "-" }}</dd>
                      <dt>{{ renting_labels.axiloc_creation_date }} :</dt>
                      <dd>{{ rentingValues.axiloc_creation_date || "-" }}</dd>
                      <dt>{{ renting_labels.wished_delivery_date }} :</dt>
                      <dd>{{ rentingValues.wished_delivery_date || "-" }}</dd>
                      <dt>{{ renting_labels.agency_fullname }} :</dt>
                      <dd>{{ rentingValues.agency_fullname || "-" }}</dd>
                      <dt>{{ renting_labels.business_fullname }} :</dt>
                      <dd>{{ rentingValues.business_fullname || "-" }}</dd>
                      <dt>{{ renting_labels.asker_email }} :</dt>
                      <dd>
                        <a v-if="rentingValues.email" :href="'mailto:' + rentingValues.email">{{ rentingValues.email
                        }}</a>
                        <a v-else-if="rentingValues.asker_email" :href="'mailto:' + rentingValues.asker_email">{{
                          rentingValues.asker_email
                        }}</a>
                        <template v-else>-</template>
                      </dd>
                      <dt>{{ renting_labels.comment }} :</dt>
                      <dd>{{ rentingValues.comment || "-" }}</dd>
                    </dl>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="text-center bottom-link">
                    <router-link v-if="rentingValues.id" :to="{
                      name: renting_config.detailRouteName,
                      params: { id: rentingValues.id },
                    }">
                      Voir la commande
                      <i class="fa fa-chevron-right"></i>
                    </router-link>
                  </b-col>
                </b-row>
              </b-col>
              <b-col cols="4" class="d-flex flex-column">
                <div class="renting-type-container">
                  <h3>Livraison</h3>
                  <span v-if="rentingValues.renting_retrieving_place == 'agency'" class="badge badge-agency">Livraison en
                    agence</span>
                  <span v-if="rentingValues.renting_retrieving_place == 'project'" class="badge badge-project">Livraison
                    sur un chantier</span>
                  <span v-if="rentingValues.renting_retrieving_place == 'other'" class="badge badge-other">Autre
                    livraison</span>
                </div>
                <ul>
                  <li>{{ rentingValues.delivery_place }} : {{ rentingValues.delivery_address || "-" }}</li>
                  <li>{{ rentingValues.zip_code || "-" }} {{ rentingValues.city || "-" }}</li>
                </ul>
                <dl>
                  <dt>{{ renting_labels.required_truck_tailgate }}</dt>
                  <dd>{{ rentingValues.required_truck_tailgate == 1 ? "Oui" : "Non" }}</dd>
                  <dt>{{ renting_labels.reachable_by_semitrailer_truck }}</dt>
                  <dd>{{ rentingValues.reachable_by_semitrailer_truck == 1 ? "Oui" : "Non" }}</dd>
                </dl>
              </b-col>
              <b-col cols="3">
                <h3>Premier contact</h3>
                <ul>
                  <li>
                    <i class="far fa-user"></i>
                    {{ rentingValues.first_contact_name || "-" }}
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="rentingValues.first_contact_phone" :href="'tel:' + rentingValues.first_contact_phone">{{
                      rentingValues.first_contact_phone }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
                <h3>Second contact</h3>
                <ul>
                  <li>
                    <i class="far fa-user"></i>
                    {{ rentingValues.second_contact_name }}
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="rentingValues.second_contact_phone" :href="'tel:' + rentingValues.second_contact_phone">{{
                      rentingValues.second_contact_phone }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="!isBusy">
      <b-col>
        <div class="ibox">
          <div class="ibox-content divided">
            <b-row>
              <b-col cols="4" class="d-flex flex-column">
                <h3>Transporteur</h3>
                <b-row>
                  <b-col class="flex-grow-1">
                    <dl>
                      <dt>{{ labels.warehouse }} :</dt>
                      <dd>{{ values.warehouse || "-" }}</dd>
                      <dt>{{ labels.shipper }} :</dt>
                      <dd>{{ values.shipper || "-" }}</dd>
                      <dt>{{ labels.tracking_number }} :</dt>
                      <dd>{{ values.tracking_number || "-" }}</dd>
                      <dt>{{ labels.weight }} :</dt>
                      <dd>{{ values.weight ? values.weight : "-" }}</dd>
                      <dt>{{ labels.handling_unit }} :</dt>
                      <dd>{{ values.handling_unit || "-" }}</dd>
                      <dt>{{ labels.imputation_number }} :</dt>
                      <dd>{{ values.imputation_number || "-" }}</dd>
                      <dt>{{ labels.shipping_date }} :</dt>
                      <dd>{{ values.shipping_date || "-" }}</dd>
                    </dl>
                  </b-col>
                </b-row>
                <b-row v-if="rawValues.shipper && rawValues.shipper.tracking_link && values.tracking_number
                  ">
                  <b-col class="text-center bottom-link">
                    <a :href="rawValues.shipper.tracking_link + values.tracking_number" target="_blank">
                      Voir le suivi transporteur
                      <i class="fa fa-chevron-right"></i>
                    </a>
                  </b-col>
                </b-row>
              </b-col>
              <b-col cols="5">
                <h3 v-if="referencesItems && referencesItems.length">
                  Références uniques envoyées
                </h3>
                <div class="table-wrapper">
                  <b-table v-if="referencesItems && referencesItems.length" hover :fields="reference_fields"
                    :items="referencesItems">
                    <template slot="product" slot-scope="data">
                      <router-link :to="{ name: 'ProductDetail', params: { id: data.item.id } }">{{
                        data.value
                      }}</router-link>
                    </template>
                    <template slot="internal_code" slot-scope="data">
                      <router-link :to="{ name: 'ReferenceDetail', params: { id: data.item.id } }">{{ data.value }}
                      </router-link>
                    </template>
                  </b-table>
                </div>
                <h3 v-if="productsItems && productsItems.length">Produits à la vente envoyés</h3>
                <div class="table-wrapper">
                  <b-table v-if="productsItems && productsItems.length" hover :fields="product_fields"
                    :items="productsItems">
                    <template slot="product" slot-scope="data">
                      <router-link :to="{ name: 'ProductDetail', params: { id: data.value.id } }">{{
                        data.value.name
                      }}</router-link>
                    </template>
                  </b-table>
                </div>
              </b-col>
              <b-col cols="3">
                <h3>{{ labels.comment }}</h3>
                <ul>
                  <li>{{ values.comment }}</li>
                </ul>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>

    <AdminModalForm v-if="config.useNewModal || config.useEditModal" :config="config" :id_to_edit="id_to_edit" />
  </b-container>
</template>

<script>
import config from "./config";
import reference_config from "@/components/Reference/config";
import renting_preparation_product_config from "@/components/RentingPreparationProduct/config";
import renting_config from "@/components/Renting/config";
import AdminModalForm from "@/components/Global/AdminModalForm";

export default {
  name: "ShippingDetail",
  components: {
    AdminModalForm,
  },
  data() {
    return {
      config: config,
      reference_config: reference_config,
      renting_preparation_product_config: renting_preparation_product_config,
      renting_config: renting_config,
      labels: {},
      renting_labels: {},
      reference_fields: [],
      product_fields: [],
      isBusy: false,
      id_to_edit: null,
    };
  },
  computed: {
    rawValues: function () {
      return (
        this.$store.getters[config.storeNamespace + "/getItem"](this.$route.params.id) || {
          product: {},
          status: {},
        }
      );
    },
    values: function () {
      return this.$root.formatData(
        this.rawValues,
        this.config.fields.filter((field) => field.inDetail == true)
      );
    },
    rentingValues: function () {
      return this.rawValues.renting_preparation && this.rawValues.renting_preparation.renting
        ? this.$root.formatData(
            this.rawValues.renting_preparation.renting,
            this.renting_config.fields.filter((field) => field.inDetail == true)
          )
        : {};
    },
    referencesItems: function () {
      return this.rawValues.renting_preparation &&
        this.rawValues.renting_preparation.unique_references
        ? this.$root.formatDatas(
            this.rawValues.renting_preparation.unique_references,
            this.reference_config.fields.filter((field) => field.inDetail == true)
          )
        : [];
    },
    productsItems: function () {
      return this.rawValues.renting_preparation && this.rawValues.renting_preparation.products
        ? this.rawValues.renting_preparation.products.filter(
            (item) => item.product.type == "selling"
          )
        : [];
    },
    exportPdfData: function () {
      let exportPdfData = {
        title: this.config.pdf_export_title
          ? this.config.pdf_export_title(this.rawValues, this.values, this)
          : this.values[this.config.titleField],
        link: this.config.pdf_export_link
          ? this.config.pdf_export_link(this.rawValues, this.values, this)
          : null,
        infos: [],
      };

      if (this.config.pdf_export_logo) {
        exportPdfData.logo = this.config.pdf_export_logo;
      }

      if (this.config.pdf_export_extra_content_before) {
        exportPdfData.extra_content_before = this.config.pdf_export_extra_content_before(
          this.rawValues,
          this.values,
          this
        );
      }

      if (this.config.pdf_export_custom_main_content) {
        exportPdfData.custom_main_content = this.config.pdf_export_custom_main_content(
          this.rawValues,
          this.values,
          this
        );
      } else {
        this.config.fields
          .filter((field) => field.inPdfExport == true)
          .forEach((field) => {
            exportPdfData.infos.push([
              field.label + " :",
              field.type == "yesno"
                ? this.values[field.name] == true
                  ? "Oui"
                  : "Non"
                : this.values[field.name] || null,
            ]);
          });
      }

      exportPdfData.lists = [];
      if (this.config.pdf_export_extra_lists) {
        exportPdfData.lists.push(
          ...this.config.pdf_export_extra_lists(this.rawValues, this.values, this)
        );
      }

      if (this.config.pdf_export_extra_content_after) {
        exportPdfData.extra_content_after = this.config.pdf_export_extra_content_after(
          this.rawValues,
          this.values,
          this
        );
      }

      return exportPdfData;
    },
  },
  mounted() {
    // Chargement des champs à afficher dans le détail depuis la config
    // Et récupération des libellés
    config.fields
      .filter((field) => field.inDetail == true)
      .forEach((field) => {
        this.labels[field.name] = field.label;
      });

    this.isBusy = true;
    this.$store
      .dispatch(config.storeNamespace + "/getItem", {
        id: this.$route.params.id,
      })
      .then(() => {
        // Test si le contenu demandé est bien trouvé
        let self = this;
        if (Object.entries(this.rawValues).length <= 2) {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
          );
          setTimeout(function () {
            self.$router.go(-1);
          }, 5000);
          return;
        }
        this.isBusy = false;
      });

    // Récupération des libellés des demandes de loc
    renting_config.fields.forEach((field) => {
      this.renting_labels[field.name] = field.label;
    });

    // Préparation des champs pour la liste des références
    let reference_fields = reference_config.fields
      .filter((field) => ["product", "internal_code"].includes(field.name))
      .map((field) => ({
        key: field.name,
        label: field.label,
      }));
    this.reference_fields = reference_fields;

    // Préparation des champs pour la liste des produits
    let product_fields = renting_preparation_product_config.fields
      .filter((field) => ["product", "quantity"].includes(field.name))
      .map((field) => ({
        key: field.name,
        label: field.label,
      }));
    this.product_fields = product_fields;
  },
  methods: {
    openFormModal(id) {
      this.id_to_edit = id;
      this.$root.$emit("bv::show::modal", "admin_modal_form");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.badge {
  &.badge-axiloc {
    background-color: $primary;
    color: #ffffff;
  }

  &.badge-agency {
    background-color: $blue;
    color: #ffffff;
  }

  &.badge-project {
    background-color: $orange;
    color: #ffffff;
  }

  &.badge-other {
    background-color: $purple;
    color: #ffffff;
  }
}

.renting-type-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}</style>




// WEBPACK FOOTER //
// src/components/Shipping/ShippingDetail.vue